import React, { useEffect, useState } from "react";
import { Download, FileText, FileSpreadsheet, IndianRupee, Loader2 } from "lucide-react";
import { api, API } from "../../lib/api";
import { compact, rupee, rupee2 } from "../../lib/format";
import { StatCard, Panel, DataTable, EntitySelect } from "./ui";
import { Header } from "./CorporateDashboard";

const today = () => new Date().toISOString().slice(0, 10);

export default function Reports({ ctx, user, mode }) {
  const fcs = ctx.foodcourts;
  const [fcId, setFcId] = useState(user.foodcourt_id || fcs[0]?.id);
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const token = localStorage.getItem("fundle_token");

  useEffect(() => {
    setData(null);
    if (mode === "dsr") {
      api.get(`/reports/dsr`, { params: { foodcourt_id: fcId, date } }).then((r) => setData(r.data));
    } else {
      api.get(`/reports/settlement`, { params: { foodcourt_id: fcId, date } }).then((r) => setData(r.data));
    }
  }, [fcId, date, mode]);

  const download = async (fmt) => {
    const base = mode === "dsr"
      ? `${API}/reports/dsr/export?foodcourt_id=${fcId}&date=${date}&format=${fmt}`
      : `${API}/reports/settlement/export?foodcourt_id=${fcId}&date=${date}&format=${fmt}`;
    const res = await fetch(base, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${mode}_${date}.${fmt === "xlsx" ? "xlsx" : fmt}`;
    a.click(); URL.revokeObjectURL(url);
  };

  const controls = (
    <div className="flex flex-wrap items-center gap-3">
      <EntitySelect label="Food Court" value={fcId} onChange={setFcId} testid="report-fc-select"
        options={fcs.map((f) => ({ value: f.id, label: `${f.mall_name} · ${f.name}` }))} />
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="report-date"
        className="bg-[#16336B] border hairline rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:border-amber-500 outline-none" />
      <div className="flex gap-2">
        {[["csv", FileText], ["xlsx", FileSpreadsheet], ["pdf", Download]].map(([f, Icon]) => (
          <button key={f} onClick={() => download(f)} className="card-surface rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 hover:border-amber-500 transition-colors" data-testid={`export-${f}`}>
            <Icon size={14} className="text-amber-400" /> {f.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );

  if (!data || (mode === "dsr" && !data.summary) || (mode === "settlement" && !data.totals))
    return <div className="space-y-6"><Header title={mode === "dsr" ? "Daily Sales Report" : "Settlement Ledger"} right={controls} /><Loader2 className="animate-spin text-amber-400" /></div>;

  if (mode === "dsr") {
    const s = data.summary;
    return (
      <div className="space-y-6">
        <Header title={`Daily Sales Report — ${data.foodcourt?.name || ""}`} sub={date} right={controls} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={IndianRupee} label="Gross Sales" value={compact(s.gross_sales)} testid="dsr-gross" />
          <StatCard label="Net Collected" value={compact(s.net_collected)} accent="emerald" />
          <StatCard label="GST" value={rupee(s.gst)} accent="blue" />
          <StatCard label="Orders / Customers" value={`${s.orders} / ${s.customers}`} accent="gold" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="AOV" value={rupee(s.aov)} />
          <StatCard label="Items Sold" value={s.items_sold} />
          <StatCard label="Packaging" value={rupee(s.packaging)} />
          <StatCard label="Restaurants" value={s.restaurant_count} />
        </div>
        <Panel title="Restaurant-wise DSR">
          <DataTable testid="dsr-table"
            columns={[
              { key: "restaurant", label: "Restaurant" },
              { key: "group", label: "Group" },
              { key: "orders", label: "Orders" },
              { key: "gross", label: "Gross", render: (r) => <span className="gold-text font-semibold">{rupee(r.gross)}</span> },
              { key: "net", label: "Net", render: (r) => rupee(r.net) },
              { key: "aov", label: "AOV", render: (r) => rupee(r.aov) },
            ]} rows={data.restaurants} />
        </Panel>
      </div>
    );
  }

  const t = data.totals;
  return (
    <div className="space-y-6">
      <Header title="Settlement Ledger" sub={`${date} · commission, gateway & platform deductions per operator`} right={controls} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Gross Collected" value={compact(t.gross)} testid="settle-gross" />
        <StatCard label="Commission" value={rupee(t.commission)} accent="red" />
        <StatCard label="Gateway + Platform" value={rupee(t.gateway_fee + t.platform_fee)} accent="red" />
        <StatCard label="Net Payable" value={compact(t.net_payable)} accent="emerald" />
      </div>
      <Panel title="Per-Restaurant Settlement">
        <DataTable testid="settlement-table"
          columns={[
            { key: "restaurant", label: "Restaurant" },
            { key: "group", label: "Group" },
            { key: "gross", label: "Gross", render: (r) => rupee2(r.gross) },
            { key: "commission", label: "Commission", render: (r) => <span className="text-red-400">-{rupee2(r.commission)}</span> },
            { key: "gateway_fee", label: "Gateway", render: (r) => <span className="text-red-400">-{rupee2(r.gateway_fee)}</span> },
            { key: "platform_fee", label: "Platform", render: (r) => <span className="text-red-400">-{rupee2(r.platform_fee)}</span> },
            { key: "refund", label: "Refund", render: (r) => rupee2(r.refund) },
            { key: "net_payable", label: "Net Payable", render: (r) => <span className="gold-text font-semibold">{rupee2(r.net_payable)}</span> },
          ]} rows={data.rows} />
      </Panel>
    </div>
  );
}
